import { useState } from "react";
import { BookOpen, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect, PageHeader } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ClassFormDialog } from "@/components/classes/ClassFormDialog";
import { ClassDetailDialog } from "@/components/classes/ClassDetailDialog";
import { useFetch } from "@/hooks/useFetch";
import { addDays, brl, CLASS_STATUS, fmtDate, toISO } from "@/lib/format";

export default function Lessons() {
  const [start, setStart] = useState(toISO(addDays(new Date(), -30)));
  const [end, setEnd] = useState(toISO(addDays(new Date(), 30)));
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const { data, loading, error, reload } = useFetch(`/classes?start=${start}&end=${end}${status ? `&status=${status}` : ""}`);

  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Aulas" subtitle="Lista completa de aulas, presença e anotações"
        actions={<Button onClick={() => setOpen(true)} data-testid="lessons-new-btn"><Plus className="mr-2 h-4 w-4" />Nova aula</Button>} />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Field label="De"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="lessons-start-input" /></Field>
        <Field label="Até"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="lessons-end-input" /></Field>
        <Field label="Status">
          <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lessons-status-filter">
            <option value="">Todos</option>
            {Object.entries(CLASS_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </NativeSelect>
        </Field>
      </div>
      {error ? <ErrorState error={error} onRetry={reload} /> : loading && !data ? <LoadingState /> : data.length === 0 ? (
        <EmptyState icon={BookOpen} title="Nenhuma aula no período" action={<Button onClick={() => setOpen(true)} data-testid="lessons-empty-new-btn">Agendar aula</Button>} />
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full min-w-[680px] text-left text-sm" data-testid="lessons-table">
            <thead><tr className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-3">Data</th><th className="p-3">Horário</th><th className="p-3">Aluno</th><th className="p-3">Disciplina</th><th className="p-3">Tipo</th><th className="p-3 text-right">Valor</th><th className="p-3">Status</th>
            </tr></thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} onClick={() => setDetail(c.id)} className="cursor-pointer border-b border-border/50 last:border-0 hover:bg-muted/30" data-testid={`lesson-row-${c.id}`}>
                  <td className="p-3 font-mono">{fmtDate(c.date)}</td><td className="p-3 font-mono text-primary">{c.start_time}</td>
                  <td className="p-3 font-medium">{c.student_name}</td><td className="p-3">{c.subject}</td>
                  <td className="p-3 text-muted-foreground">{c.type === "online" ? "Online" : "Presencial"}</td>
                  <td className="p-3 text-right font-mono">{brl(c.price)}</td><td className="p-3"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ClassFormDialog open={open} onOpenChange={setOpen} onSaved={reload} />
      <ClassDetailDialog open={!!detail} lessonId={detail} onOpenChange={(o) => !o && setDetail(null)} onChanged={reload} />
    </div>
  );
}
